import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { LaunchDarklyFlagValue } from '../launch-darkly.client';
import { FeatureFlagsActions } from './feature-flags.actions';
import { QUICK_ACTIONS_DEFAULT } from './feature-flags.constants';
import { hasAnyTrueValue } from './feature-flags.helper';
import {
  FeatureFlagQuickActions,
  FeatureFlagShareAssistant,
} from './feature-flags.interfaces';

export const featureFlagsFeatureKey = 'featureFlags';

export enum FeatureFlagKeyEnum {
  'ff-display-document-sources' = 'ff-display-document-sources',
  'ff-bug-reporting-button-jira' = 'ff-bug-reporting-button-jira',
  'ff-document-viewer-highlight-text' = 'ff-document-viewer-highlight-text',
  'ff-quick-actions' = 'ff-quick-actions',
  'ff-navigate-between-highlights' = 'ff-navigate-between-highlights',
  'ff-share-assistant' = 'ff-share-assistant',
  'ff-new-release-announcement' = 'ff-new-release-announcement',
  'ff-deepgram' = 'ff-deepgram',
  'ff-stt-debug-log' = 'ff-stt-debug-log',
  'ff-reordering-my-favourites-assistants' = 'ff-reordering-my-favourites-assistants',
  'ff-advanced-assistant-builder' = 'ff-advanced-assistant-builder',
  'ff-model-thinking-streaming' = 'ff-model-thinking-streaming',
  'ff-chat-title-in-browser-tab' = 'ff-chat-title-in-browser-tab',
  'ff-mcp-connectors' = 'ff-mcp-connectors',
}
export type FeatureFlagKey = `${FeatureFlagKeyEnum}`;

export interface FeatureFlagView extends LaunchDarklyFlagValue {
  key: string;
  value: unknown;
}

export interface FeatureFlagsState extends EntityState<FeatureFlagView> {
  /** Flags are evaluated for the signed-in user. Until then they hold the anonymous boot values. */
  ready: boolean;
  initError: string | null;
  error: string | null;
}

export const entityAdapter: EntityAdapter<FeatureFlagView> =
  createEntityAdapter<FeatureFlagView>({
    selectId: (flag) => flag.key,
  });

export const initialFeatureFlagsState: FeatureFlagsState =
  entityAdapter.getInitialState({
    ready: false,
    initError: null,
    error: null,
  });

export const featureFlagsFeature = createFeature({
  name: featureFlagsFeatureKey,
  reducer: createReducer(
    initialFeatureFlagsState,
    on(FeatureFlagsActions.loadFeatureFlags, (state, { featureFlags }) =>
      entityAdapter.setAll(featureFlags, state),
    ),
    on(FeatureFlagsActions.userIdentified, (state, { featureFlags }) =>
      entityAdapter.upsertMany(featureFlags, { ...state, ready: true }),
    ),
    on(FeatureFlagsActions.onChangeSyncSuccess, (state, { featureFlags }) =>
      entityAdapter.upsertMany(featureFlags, state),
    ),
    on(FeatureFlagsActions.onError, (state, { error }) => ({
      ...state,
      error: error.message,
    })),
  ),
  extraSelectors: ({ selectFeatureFlagsState, selectReady }) => {
    const entitySelectors = entityAdapter.getSelectors(selectFeatureFlagsState);
    const selectFeatureFlag = <T = boolean>(featureFlagKey: FeatureFlagKey) =>
      createSelector(
        entitySelectors.selectEntities,
        (featureFlags) => featureFlags[featureFlagKey]?.value as T,
      );

    return {
      ...entitySelectors,
      selectFeatureFlag,
      /** SB-3651 — the live turn, streamed answer and saved traces. */
      selectThinkingStreaming: createSelector(
        selectFeatureFlag<boolean | undefined>('ff-model-thinking-streaming'),
        (enabled) => enabled === true,
      ),
      /**
       * `ff-mcp-connectors` for the signed-in user. Off until LaunchDarkly has evaluated it for them:
       * the boot value can be on for someone whose own targeting turns it off.
       */
      selectMcpConnectors: createSelector(
        selectReady,
        selectFeatureFlag<boolean | undefined>('ff-mcp-connectors'),
        (ready, enabled) => ready && enabled === true,
      ),
      selectQuickActions: createSelector(
        selectFeatureFlag<FeatureFlagQuickActions>('ff-quick-actions'),
        (ff: FeatureFlagQuickActions = QUICK_ACTIONS_DEFAULT) => {
          return {
            ...ff,
            hasAnyTrueValue: hasAnyTrueValue(ff),
            export: {
              ...ff.export,
              hasAnyTrueValue: hasAnyTrueValue(ff.export),
            },
            starsAi: {
              ...ff.starsAi,
              hasAnyTrueValue: hasAnyTrueValue(ff.starsAi),
              changeTone: {
                ...ff.starsAi.changeTone,
                hasAnyTrueValue: hasAnyTrueValue(ff.starsAi.changeTone),
              },
              improveWriting: {
                ...ff.starsAi.improveWriting,
                hasAnyTrueValue: hasAnyTrueValue(ff.starsAi.improveWriting),
              },
              translate: {
                ...ff.starsAi.translate,
                hasAnyTrueValue: hasAnyTrueValue(ff.starsAi.translate),
              },
            },
          };
        },
      ),
      selectShareAssistant: createSelector(
        selectFeatureFlag<FeatureFlagShareAssistant | undefined>(
          'ff-share-assistant',
        ),
        (ff) => {
          if (!ff?.enabled) {
            return {
              enabled: false,
              everyoneInMyOrganization: false,
            } satisfies FeatureFlagShareAssistant;
          }
          return ff;
        },
      ),
    };
  },
});
